import TripPackage from '../TripPackage';
import type TripPriceType from '../TripPriceType';
import TripSegment from '../TripSegment';
import { TripInfoComponent, type TripPackageDetails } from '../types';
import Bus from './Bus';
import BusPriceClass from './BusPriceClass';
import ItineraryOrder from '@itinerary/ItineraryOrder';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import {
    ExtraInfoIcons,
    ExtraInfoLang,
    type CardLabel,
} from '@itinerary/types';
import { type GetServicesResultBus } from '@clients/BusApi';
import {
    type AutomaticReserveDemandItemRequest,
    ServiceType,
} from '@clients/PricetrackApi';
import BusAutoPriceClass from '@itinerary/trip/bus/BusAutoPriceClass';
import { PaxType, type PaxTypeTitle } from '@utils/types';
import { ReserveType } from '@utils/itinerary';

export default class BusPackage extends TripPackage<Bus, BusPriceClass> {
    override get tripPackageDetails(): TripPackageDetails[] {
        if (!this.bus.needsSelectSeat) {
            return super.tripPackageDetails;
        }
        return [
            ...super.tripPackageDetails,
            {
                title: 'نقشه صندلی‌ها',
                component: TripInfoComponent.BusSeatsSmall,
            },
        ];
    }

    /**
     * Points of the trip packages, based on passengers' opinions
     */
    override get points(): number | null {
        return this.bus.isCar
            ? this.bus.precisionPoints ?? null
            : this.bus.points ?? null;
    }

    override get raiseTitle() {
        if (!this.bus.isCar) {
            return super.raiseTitle;
        }
        const origin = this.origin.replace(/ *\([^)]*\)*/g, '');
        const destination = this.destination.replace(/ *\([^)]*\)*/g, '');
        return `انتخاب سرویس ${origin} به ${destination}`;
    }

    override prices: TripPriceType<BusPriceClass>[] = [];

    /**
     * The raw bus object, returned by the server
     */
    _bus?: GetServicesResultBus;

    get bus() {
        return this._bus!;
    }

    override totalCount = 0;

    providerName = '';

    distance: number | null = null;

    _duration = 0;

    override get duration() {
        return this._duration;
    }

    get serviceLabel() {
        return this.bus.isCar ? 'تاکسی' : 'اتوبوس';
    }

    override set duration(val: number) {
        this._duration = val;
    }

    override segments: TripSegment<Bus>[] = [];

    override key = '';

    override callToReserve = false;

    description = '';

    override passengerCountEditTypes: PaxTypeTitle[] = [];

    constructor(
        bus: GetServicesResultBus | null = null,
        totalCount = 0,
        public isLateralTaxi = false,
        public reserveType: ReserveType = ReserveType.Bus
    ) {
        super();
        if (bus === null) {
            return;
        }
        this.concreteType = ReserveType.Bus;
        this._bus = bus;
        this.totalCount = totalCount;
        const trip = new Bus(this.bus);
        this._duration = trip.duration;
        this.distance = bus.distance || null;
        this.hasAutoReserve =
            reserveType === ReserveType.Bus && !this.reservable;
        if (this.hasAutoReserve || reserveType === ReserveType.Bus) {
            this.passengerCountEditTypes = [
                {
                    code: PaxType.Adult,
                    title: 'صندلی',
                },
            ];
        }
        const price = this.hasAutoReserve
            ? new BusAutoPriceClass(bus)
            : new BusPriceClass(bus, reserveType);
        this.prices = [
            {
                classes: [price],
            },
        ];
        this.segments = this.bus
            ? [
                  new TripSegment(
                      [trip],
                      [
                          { title: `مقصد نهایی ${bus.finalDestinationCity}` },
                          ...(bus.description
                              ? [{ title: bus.description }]
                              : []),
                          ...(this.pointOption ? [this.pointOption] : []),
                      ],
                      ''
                  ),
              ]
            : [];
        this.defaultPrice = this.selectedPrice = price;
        this.providerName = bus.providerName ?? '';
        this.key = trip.key;
        this.uniqueId = trip.key;
        this.description = bus.description ?? '';
    }

    private get busTypeTitle() {
        return this.bus.isVIP ? 'وی‌آی‌پی' : 'عادی';
    }

    override get extraInfo() {
        const infoConfig = {
            icon: this.bus.isCar
                ? ExtraInfoIcons.taxiVip
                : ExtraInfoIcons.busVip,
            lang: ExtraInfoLang.fa,
        };
        if (!this.bus.shortTitle) {
            return undefined;
        }
        return {
            text: this.bus.shortTitle,
            ...infoConfig,
        };
    }

    override get hasPassengerDelete() {
        return !!this.passengerCountEditTypes?.length;
    }

    override get autoReserveDemandParams(): AutomaticReserveDemandItemRequest {
        return {
            arrivalTime: formatISO(new Date(this.arrivalDateTime)).slice(0, -1),
            departureTime: formatISO(new Date(this.departureDateTime)).slice(
                0,
                -1
            ),
            serviceType: ServiceType.NUMBER_2,
            serviceId: this.serviceId?.toString(),
            serviceNumber: null,
            providerName: this._bus?.superCorporation ?? '',
            providerId: this._bus?.superCorporationID?.toString() ?? '',
        };
    }

    override get labels(): CardLabel[] {
        const labels: CardLabel[] = [];
        labels.push({
            title: this.busTypeTitle,
            backgroundColor: 'var(--blue-100)',
            color: 'var(--blue-700)',
        });
        if (this.reserveType === ReserveType.Taxi) {
            labels.push({
                title: 'تاکسی رسمی',
                backgroundColor: '#FDF5EA',
                color: '#EE9840',
            });
        }
        if (!this.bus.shortTitle && this.bus.busType) {
            labels.push({
                title: this.bus.busType,
            });
        }
        labels.push(
            ...(this.bus.lables || []).map(label => ({
                title: label.title ?? '',
            }))
        );
        if (this.providerName) {
            labels.push({
                title: this.providerName,
            });
        }
        if (
            this.firstTrip.finalDestination &&
            !this.destination.includes(this.firstTrip.finalDestination)
        ) {
            labels.push({
                title: `مقصد نهایی ${this.firstTrip.finalDestination}`,
                order: 0,
            });
        }
        return labels;
    }

    override get totalPayableForCheck(): number | null {
        return null;
    }

    override get reservable() {
        if (!this.bus.reservable) {
            return false;
        }
        return (this.bus.capacity ?? 0) > 0 || !this.bus.needsSelectSeat;
    }

    override get hasSeatSelection() {
        return (
            !!this.bus.needsSelectSeat &&
            !this.bus.isExclusive &&
            !this.isSelectedToBeExclusive
        );
    }

    override get totalPriceForCheck(): number | null {
        return null;
    }

    get isSelectedToBeExclusive() {
        return !!this.selectedPrice?.isSelectedToBeExclusive;
    }

    override get order() {
        return new ItineraryOrder(
            dayConfig.gregory(this.bus.departureTime).valueOf(),
            this.bus.price ?? 0,
            (other: ItineraryPackage): number => {
                const otherPackage = other as BusPackage;
                return (
                    (this.bus.points ?? 0) - (otherPackage.bus.points ?? 0) ||
                    this.bus.superCorporation?.localeCompare(
                        otherPackage.bus.superCorporation ?? ''
                    ) ||
                    this.bus.fromTerminal?.localeCompare(
                        otherPackage.bus.fromTerminal ?? ''
                    ) ||
                    0
                );
            }
        );
    }

    override get buyText() {
        if (this.autoReserveEnabled) {
            return 'رزرو خودکار';
        }
        if (!this.bus.reservable) {
            return 'قطعی سرور';
        }
        return super.buyText;
    }

    override get reserveButtonEnabled() {
        if (this.capacity && this.capacity < this.totalCount) {
            return false;
        }
        return super.reserveButtonEnabled;
    }

    override get reserveError() {
        if (
            !this.bus.isExclusive &&
            this.capacity &&
            this.capacity < this.totalCount
        ) {
            return 'ظرفیت موجود کمتر از تعداد مسافران شماست!';
        }
        return super.reserveError;
    }

    /**
     * The tooltip to be shown on reserve button when is being hovered
     */
    override get buyTooltip() {
        if (!this.bus.reservable) {
            return 'در حال حاضر به‌دلیل قطعی ارتباط با تامین‌کننده، رزرو این سرویس مقدور نمی‌باشد.';
        }
        return '';
    }

    static revive(data: BusPackage) {
        const newPack = new BusPackage();
        Object.assign(newPack, data);
        if (data.selectedPrices?.length) {
            newPack.selectedPrices = [];
            data.selectedPrices.forEach(price => {
                const priceClass = newPack?.autoReserveEnabled
                    ? new BusAutoPriceClass()
                    : new BusPriceClass();
                Object.assign(priceClass, price);
                newPack.selectedPrices!.push(priceClass);
            });
        }
        newPack.segments[0].trips[0].departureDateTime = new Date(
            newPack.segments[0].trips[0].departureDateTime
        );
        newPack.segments = newPack.segments.map(
            currentSegment =>
                new TripSegment(
                    currentSegment.trips.map(t => Object.assign(new Bus(), t)),
                    currentSegment.options,
                    currentSegment.stopsDescription,
                    currentSegment.collapseTrips,
                    currentSegment.compactOptions
                )
        );
        return newPack;
    }
}
